import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Search, Clock } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { SectionHeading, EmptyState } from "@/components/shared/SectionHeading";
import { Input } from "@/components/ui/input";

export default function Education() {
  const { t } = useLang();
  useSEO("Trading Education", "Trading education — structured articles, guides and lessons from INVIXA.");
  const [articles, setArticles] = useState(null);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/education/articles").then((r) => setArticles(r.data)).catch(() => setArticles([]));
    api.get("/education/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!articles) return [];
    return articles.filter((a) => {
      const matchQ = !q || a.title.toLowerCase().includes(q.toLowerCase()) || (a.excerpt || "").toLowerCase().includes(q.toLowerCase());
      const matchCat = cat === "all" || a.category_id === cat;
      return matchQ && matchCat;
    });
  }, [articles, q, cat]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
      <SectionHeading eyebrow={t("nav_education")} title="Trading Education Hub" subtitle="Structured lessons, guides and market insights to develop as a trader." />

      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} data-testid="edu-search" className="pl-10 bg-white/5 border-white/10" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setCat("all")} data-testid="edu-cat-all" className={`px-4 py-2 rounded-full text-sm border ${cat === "all" ? "border-brand-cyan text-brand-cyan bg-brand-cyan/10" : "border-white/10 text-slate-400"}`}>All</button>
          {categories.map((c) => (
            <button key={c.id} onClick={() => setCat(c.id)} data-testid={`edu-cat-${c.id}`} className={`px-4 py-2 rounded-full text-sm border ${cat === c.id ? "border-brand-cyan text-brand-cyan bg-brand-cyan/10" : "border-white/10 text-slate-400"}`}>{c.name}</button>
          ))}
        </div>
      </div>

      <div className="mt-10">
        {articles === null ? <div className="text-center text-slate-500 py-20">{t("loading")}</div>
          : filtered.length === 0 ? <EmptyState message="No articles published yet." icon={BookOpen} />
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((a) => (
                <Link key={a.id} to={`/education/${a.slug}`} className="card-surface hover-glow rounded-2xl overflow-hidden group" data-testid={`edu-article-${a.id}`}>
                  <div className="h-44 bg-brand-elevated overflow-hidden">
                    {a.cover_image_file_id ? <img src={fileUrl(a.cover_image_file_id)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <div className="w-full h-full flex items-center justify-center"><BookOpen className="h-8 w-8 text-slate-600" /></div>}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-[11px] font-mono text-brand-cyan uppercase"><Clock className="h-3.5 w-3.5" /> {a.reading_time || 5} min</div>
                    <h3 className="mt-2 font-display font-semibold text-white line-clamp-2">{a.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 line-clamp-2">{a.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
